<template>
  <div class="line-style-editor">
    <!-- Preview -->
    <div class="preview-container mb-4">
      <svg width="100%" height="72" viewBox="0 0 300 72" xmlns="http://www.w3.org/2000/svg">
        <!-- Static map behind the line so the colour/thickness/opacity
             customisation previews over a realistic map (not a flat grid). -->
        <rect width="300" height="72" fill="#1e2530" />
        <image
          :href="previewMapBg"
          x="0" y="0" width="300" height="72"
          preserveAspectRatio="xMidYMid slice"
        />
        <!-- Slight darkening keeps lighter line colours legible over the map. -->
        <rect width="300" height="72" fill="#10141b" opacity="0.18" />
        <path
          d="M 15 52 Q 55 18 95 42 Q 135 62 175 28 Q 215 8 260 36 L 285 26"
          :stroke="color"
          :stroke-width="previewStrokeWidth"
          :stroke-opacity="opacity / 100"
          stroke-linecap="round"
          stroke-linejoin="round"
          fill="none"
        />
      </svg>
    </div>

    <!-- Colour -->
    <div class="style-section">
      <p class="section-label">{{ colorLabel }}</p>
      <div class="color-swatches">
        <button
          v-for="option in colorOptions"
          :key="option"
          class="color-swatch"
          :class="{ 'color-swatch--active': color.toUpperCase() === option.toUpperCase() }"
          :style="{ background: option }"
          :aria-label="option"
          @click="$emit('update:color', option)"
        />
      </div>
    </div>

    <!-- Thickness -->
    <div class="style-section">
      <p class="section-label">{{ thicknessLabel }}</p>
      <div class="thickness-options">
        <button
          v-for="t in thicknessOptions"
          :key="t.value"
          class="thickness-btn"
          :class="{ 'thickness-btn--active': thickness === t.value }"
          @click="$emit('update:thickness', t.value)"
        >
          {{ t.label }}
        </button>
      </div>
    </div>

    <!-- Opacity -->
    <div class="style-section">
      <div class="d-flex justify-content-between align-items-center mb-2">
        <p class="section-label mb-0">{{ opacityLabel }}</p>
        <span class="opacity-value">{{ opacity }}%</span>
      </div>
      <input
        type="range"
        class="opacity-slider w-100"
        min="10"
        max="100"
        step="1"
        :value="opacity"
        @input="$emit('update:opacity', +($event.target as HTMLInputElement).value)"
      />
    </div>

    <!-- Anything caller-specific (e.g. the route-only mile-markers toggle) -->
    <slot />

    <!-- Reset -->
    <button class="reset-btn mt-3" @click="$emit('reset')">Reset to default</button>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';

/** The shared palette. Kept identical to iOS `RouteStylePreference.predefinedColors`
 *  and Android `RouteStylePref.AVAILABLE_COLORS` so a colour picked on one
 *  platform is offered on the others. */
export const LINE_STYLE_COLOR_OPTIONS = [
  '#DB2B35', '#F57700', '#FDDF00',
  '#00FFFF', '#00FF8C', '#005BE4',
  '#9E00FF', '#FF00FF', '#FF6F61', '#22242A',
];

/**
 * Presentational-only line style editor: preview, colour, thickness, opacity,
 * reset. Shared by "Customise route style" and "Customise overlay line style"
 * so both screens stay the same view. Owns no persistence — the parent decides
 * where a change is stored and synced.
 */
export default defineComponent({
  name: 'LineStyleEditor',

  props: {
    color:     { type: String, required: true },
    thickness: { type: Number, required: true },
    opacity:   { type: Number, required: true }, // percent, 10-100
    colorLabel:     { type: String, default: 'Route colour' },
    thicknessLabel: { type: String, default: 'Route thickness' },
    opacityLabel:   { type: String, default: 'Route opacity' },
  },

  emits: ['update:color', 'update:thickness', 'update:opacity', 'reset'],

  data() {
    return {
      // Real (non-interactive) map tiles behind the preview via the Mapbox
      // Static Images API — a scenic outdoors area so the line style is shown
      // over an actual map. Uses the app's standard public Mapbox token.
      previewMapBg:
        'https://api.mapbox.com/styles/v1/mapbox/outdoors-v12/static/-6.4012,53.0996,12,0/600x144' +
        '?access_token=pk.eyJ1IjoiaGlpa2VyLW1hcHMiLCJhIjoiY2x0YThyZHppMWhmejJqbzExODBuY3dveiJ9.nBrtnXgbzxpAx_953HCB-w' +
        '&attribution=false&logo=false',
      colorOptions: LINE_STYLE_COLOR_OPTIONS,
      thicknessOptions: [
        { label: '0.5×', value: 0.5 },
        { label: '1×',   value: 1   },
        { label: '2×',   value: 2   },
        { label: '3×',   value: 3   },
      ],
    };
  },

  computed: {
    previewStrokeWidth(): number {
      return Math.max(2, this.thickness * 5);
    },
  },
});
</script>

<style scoped>
.preview-container {
  border-radius: 12px;
  overflow: hidden;
}

.section-label {
  font-size: 0.75rem;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-bottom: 12px;
}

.style-section {
  margin-bottom: 24px;
}

.color-swatches {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.color-swatch {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 3px solid transparent;
  cursor: pointer;
  flex-shrink: 0;
  padding: 0;
  transition: box-shadow 0.15s;
}

.color-swatch--active {
  box-shadow: 0 0 0 2px #17181d, 0 0 0 4px #fddf00;
}

.thickness-options {
  display: flex;
  gap: 8px;
}

.thickness-btn {
  flex: 1;
  padding: 8px 0;
  border: 1.5px solid #444;
  border-radius: 9999px;
  background: transparent;
  color: #aaa;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: border-color 0.15s, color 0.15s;
}

.thickness-btn--active {
  border-color: #fddf00;
  color: #fddf00;
}

.opacity-value {
  color: #fddf00;
  font-weight: 700;
  font-size: 0.9rem;
}

.opacity-slider {
  accent-color: #fddf00;
  cursor: pointer;
}

.reset-btn {
  width: 100%;
  padding: 12px;
  background: var(--gray);
  color: #fff;
  border: none;
  border-radius: 10px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;
}

.reset-btn:hover {
  background: #3a3a3a;
}
</style>
